import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import { loadById } from "../devices/deviceService";
import { getById, remediateRule, saveRule } from "./ruleService";
import { showDangerToast, showInfoToast, showSuccessToast, showWarningToast } from "../validation/ToastContainer";
import { PopupContext } from "../index";
import { getConfirmPopup } from "../view/popup/PopupContainer";
import {
    assessImpact,
    getImpactReadiness,
    loadLatestDeviceConfig
} from "./remediation/impactAnalysisService";
import ImpactAnalysisPopup from "./remediation/ImpactAnalysisPopup";
import {
    finishLocalProcessingTask,
    startLocalProcessingTask
} from "../processing-tasks/processingTasksRegistry";
// import { Name } from "../view/UserMenu";

function getImpactContentFingerprint(...values) {
    const content = values.map((value) => value || "").join("\u241f");
    let hash = 2166136261;

    for (let index = 0; index < content.length; index += 1) {
        hash ^= content.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
}

function getImpactCacheKey({ deviceId, ruleId, resultId, scanStartTime, remediationScript, remediationDesc }) {
    const contentFingerprint = getImpactContentFingerprint(remediationScript, remediationDesc);
    return `octopus.impact-analysis.v3.${deviceId ?? "na"}.${ruleId ?? "na"}.${resultId ?? "na"}.${scanStartTime ?? "na"}.${contentFingerprint}`;
}

export function RemediationModal({ deviceId, ruleId, realTime, resultId, validationResult, closeModal, currentUser, disabled }) {

    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [ruleDataChanged, setRuleDataChanged] = useState(false);
    const [device, setDevice] = useState(null);
    const [rule, setRule] = useState(null);
    const [impactState, setImpactState] = useState({
        loading: false,
        error: "",
        result: null,
        open: false,
    });
    const [impactAvailability, setImpactAvailability] = useState({
        checking: true,
        available: false,
        message: "Checking Impact Analysis availability...",
    });
    const [impactConfirmationOpen, setImpactConfirmationOpen] = useState(false);
    const { setPopup } = useContext(PopupContext);
    const impactCacheKey = getImpactCacheKey({
        deviceId,
        ruleId,
        resultId,
        scanStartTime: validationResult?.startTime,
        remediationScript: rule?.remediationScript,
        remediationDesc: rule?.remediationDesc,
    });

    useEffect(() => {
        async function load() {
            const device = await loadById(deviceId);
            const rule = await getById(ruleId);

            setDevice(device);
            setRule(rule);
            setLoading(false);
        }

        load();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [])

    useEffect(() => {
        let active = true;

        const refreshAvailability = async () => {
            const readiness = await getImpactReadiness();
            if (!active) return;
            setImpactAvailability({
                checking: false,
                available: readiness.available,
                message: readiness.message,
            });
        };

        refreshAvailability();
        const intervalId = window.setInterval(refreshAvailability, 10000);
        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, []);

    const onSave = async () => {

        const success = await saveRule(rule);
        if (success) {
            showSuccessToast('Rule saved', 'Save');
            closeModal();
        } else {
            showDangerToast('Rule was not saved', 'Save', 'Something went wrong');
        }

    }

    const onExecute = async (
        confirmationMessage = "Are you sure you want to execute the remediation script?",
        confirmationOptions = {}
    ) => {

        if (!rule.remediationScript) {
            showWarningToast("Remediation script does not specified", "Execute");
            return;
        }

        const popup = getConfirmPopup(confirmationMessage, "Execute", async () => {
            confirmationOptions.onConfirm?.();
            if (ruleDataChanged) {
                await saveRule(rule);
            }
            showInfoToast('Rule remediation started', 'Remediate');
            const processingTaskId = startLocalProcessingTask({
                id: `remediate-${deviceId}-${ruleId}-${Date.now()}`,
                type: "Remediate",
                title: "Rule Remediation",
                device: device?.hostName,
                target: device?.hostName,
                details: rule?.name || rule?.rule || rule?.description || ""
            });
            closeModal();
            try {
                const success = await remediateRule(deviceId, realTime, ruleId, resultId, currentUser);
                //alert(success)
                if (success) {
                    showSuccessToast('Rule remediation Completed', 'Remediate');
                    if (Number(realTime) === 1) {
                        history.go(0);
                    }
                    //closeModal();
                } else {
                    showDangerToast('Rule remediation execution has not started', 'Remediate', 'Something went wrong');
                }
            } finally {
                finishLocalProcessingTask(processingTaskId);
            }
        }, confirmationOptions.onCancel);

        setPopup(popup);

    }

    const onChange = (key, value) => {
        !ruleDataChanged && setRuleDataChanged(true);
        setRule({ ...rule, [key]: value })
    }

    const readCachedImpactResult = () => {
        try {
            const raw = sessionStorage.getItem(impactCacheKey);
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            console.warn("[impact-analysis] failed to read cache", error);
            return null;
        }
    };

    const writeCachedImpactResult = (result) => {
        try {
            sessionStorage.setItem(impactCacheKey, JSON.stringify(result));
        } catch (error) {
            console.warn("[impact-analysis] failed to write cache", error);
        }
    };

    const openImpactAnalysis = () => {
        console.log("[impact-analysis] modal open");
        if (loading || !device || !rule) {
            showWarningToast("Please wait until the device and rule finish loading before running impact analysis.", "Impact Analysis");
            return;
        }
        const cachedResult = readCachedImpactResult();
        if (cachedResult) {
            setImpactState({
                loading: false,
                error: "",
                result: cachedResult,
                open: true,
            });
            return;
        }

        reassessImpact();
    };

    const reassessImpact = async () => {
        console.log("[impact-analysis] reassess start");
        const processingTaskId = startLocalProcessingTask({
            id: `impact-analysis-${deviceId}-${ruleId}-${Date.now()}`,
            type: "Impact Analysis",
            title: "Remediation Impact Analysis",
            device: device?.hostName,
            target: device?.hostName,
            details: rule?.name || rule?.rule || rule?.description || ""
        });
        setImpactState((prev) => ({
            loading: true,
            error: "",
            result: prev.result,
            open: true,
        }));
        try {
            const readiness = await getImpactReadiness();
            setImpactAvailability({
                checking: false,
                available: readiness.available,
                message: readiness.message,
            });
            if (!readiness.available) {
                throw new Error(readiness.message || "Impact Analysis is currently unavailable.");
            }

            const configContext = await loadLatestDeviceConfig(device?.hostName);
            const result = await assessImpact({
                device,
                rule,
                currentUser,
                realTime,
                resultId,
                validationResult,
                deviceConfig: configContext?.snapshot || null,
            });
            writeCachedImpactResult(result);
            setImpactState({ loading: false, error: "", result, open: true });
            console.log("[impact-analysis] reassess success");
        } catch (error) {
            setImpactState({
                loading: false,
                error: error?.message || "Impact analysis failed",
                result: readCachedImpactResult(),
                open: true,
            });
        } finally {
            finishLocalProcessingTask(processingTaskId);
        }
    };

    const onProceedToRemediation = async () => {
        const band = String(impactState.result?.impactBand || "").toUpperCase();
        const score = impactState.result?.impactScore;
        const isHighRisk = band === "HIGH" || band === "CRITICAL";
        const confirmationMessage = isHighRisk
            ? `This remediation has a ${band} impact rating${score != null ? ` (${score}/100)` : ""}. Confirm that the required pre-checks, backup, alternate access, and approval are in place before execution. Do you want to continue?`
            : "Are you sure you want to execute the remediation script?";

        setImpactConfirmationOpen(true);
        await onExecute(confirmationMessage, {
            onConfirm: () => setImpactConfirmationOpen(false),
            onCancel: () => setImpactConfirmationOpen(false),
        });
    };

    const onCloseImpactAnalysis = () => {
        setImpactState((prev) => ({
            ...prev,
            loading: false,
            error: "",
            open: false,
        }));
    };

    const renderSkeletonField = (rows = 1, isScript = false) => (
        <div className="form-group modal-v2-remediation-field">
            <div className="oct_form-group">
                <div className="modal-v2-remediation-label-row">
                    <Skeleton variant="rounded" width={40} height={40} className="modal-v2-remediation-skeleton-icon" />
                    <Skeleton variant="text" width={140} height={28} />
                </div>
                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={isScript ? 180 : rows > 1 ? 120 : 52}
                    className={`modal-v2-remediation-skeleton-block${isScript ? ' is-script' : ''}`}
                />
            </div>
        </div>
    );

    return (
        <div className="modal oct_display-block modal-v2-root">
            {/* <div>
            <Name.Consumer>
                {(fname) => {
                    <h1>My Name is {fname}</h1>;
                }}
            </Name.Consumer>
            </div> */}
            <div className="modal oct_modal modal-v2-overlay">
                <div className="modal-remediation modal-dialog modal-dialog-centered modal-v2-dialog" aria-modal="true">
                    <div className="modal-content modal-v2-shell">
                        <div className="modal-v2-remediation-header">
                            <div className="modal-v2-remediation-header-inner">
                                <div className="oct_form-title mb-0 modal-v2-remediation-title">Remediation for Device {device?.hostName}</div>
                                <button type="button" className="close modal-v2-close modal-v2-remediation-close" aria-label="Close" onClick={closeModal}>
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body modal-v2-body">
                            <div className="container modal-v2-container modal-v2-form modal-v2-remediation-form">
                                <div>
                                    {loading ?
                                        <div className="modal-v2-remediation-skeleton">
                                            {renderSkeletonField()}
                                            {renderSkeletonField()}
                                            {renderSkeletonField(3)}
                                            {renderSkeletonField(6, true)}
                                        </div>
                                        :
                                        <>
                                            <div className="form-group modal-v2-remediation-field">
                                                <div className="oct_form-group">
                                                    <label className="modal-v2-remediation-label-row" htmlFor={'dd-name'}>
                                                        <span className="modal-v2-remediation-icon" aria-hidden="true">
                                                            <svg viewBox="0 0 24 24" focusable="false">
                                                                <rect x="4" y="5" width="16" height="14" rx="2"></rect>
                                                                <path d="M8 9h8"></path>
                                                                <path d="M8 15h5"></path>
                                                            </svg>
                                                        </span>
                                                        <span>Device</span>
                                                    </label>
                                                    <input type={'text'}
                                                        value={device?.hostName}
                                                        disabled={true}
                                                        className={`form-control oct_field modal-v2-remediation-input`}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group modal-v2-remediation-field">
                                                <div className="oct_form-group">
                                                    <label className="modal-v2-remediation-label-row" htmlFor={'hh-name'}>
                                                        <span className="modal-v2-remediation-icon" aria-hidden="true">
                                                            <svg viewBox="0 0 24 24" focusable="false">
                                                                <circle cx="12" cy="12" r="8"></circle>
                                                                <path d="M4 12h16"></path>
                                                                <path d="M12 4a12 12 0 0 1 0 16"></path>
                                                                <path d="M12 4a12 12 0 0 0 0 16"></path>
                                                            </svg>
                                                        </span>
                                                        <span>IP Address</span>
                                                    </label>
                                                    <input type={'text'}
                                                        value={device?.mngtIpAddress}
                                                        disabled={true}
                                                        className={`form-control oct_field modal-v2-remediation-input`}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group modal-v2-remediation-field">
                                                <label className="oct_form-title mb-3 modal-v2-remediation-label-row">
                                                    <span className="modal-v2-remediation-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" focusable="false">
                                                            <path d="M8 4h6l4 4v12H6V4z"></path>
                                                            <path d="M14 4v4h4"></path>
                                                            <path d="M9 12h6"></path>
                                                            <path d="M9 16h6"></path>
                                                        </svg>
                                                    </span>
                                                    <span>Remediation Description</span>
                                                </label>
                                                <textarea className="remediation_text-area w-100 modal-v2-remediation-textarea"
                                                    value={rule?.remediationDesc ? rule.remediationDesc : ''}
                                                    disabled={disabled}
                                                    onChange={disabled ? null : (e) => onChange('remediationDesc', e.target.value)}
                                                    rows="3" />
                                            </div>
                                            <div className="form-group modal-v2-remediation-field">
                                                <label className="oct_form-title mb-3 modal-v2-remediation-label-row">
                                                    <span className="modal-v2-remediation-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" focusable="false">
                                                            <path d="M8 9L4 12l4 3"></path>
                                                            <path d="M16 9l4 3-4 3"></path>
                                                            <path d="M13 6l-2 12"></path>
                                                        </svg>
                                                    </span>
                                                    <span>Script</span>
                                                </label>
                                                <textarea className="remediation_text-area w-100 modal-v2-remediation-textarea modal-v2-remediation-script"
                                                    value={rule?.remediationScript ? rule.remediationScript : ''}
                                                    disabled={disabled}
                                                    onChange={disabled ? null : (e) => onChange('remediationScript', e.target.value)}
                                                    rows="6" />
                                            </div>

                                        </>
                                    }
                                </div>
                            </div>
        </div>
        <div className="modal-footer modal-v2-footer">
                            <div className="modal-v2-footer-note">
                                {!impactAvailability.checking && !impactAvailability.available
                                    ? impactAvailability.message
                                    : ""}
                            </div>
                            <div className="modal-v2-footer-actions">
                                <div className={`btn oct_btn-form modal-v2-btn modal-v2-btn-outline`} onClick={closeModal}>Cancel
                                </div>
                                <div className={`btn oct_btn-form modal-v2-btn modal-v2-btn-primary ${disabled && 'disabled'}`}
                                onClick={disabled ? null : onSave}>Save
                                </div>
                                <div
                                title={!impactAvailability.available ? impactAvailability.message : ""}
                                className={`btn oct_btn-form modal-v2-btn modal-v2-btn-primary ${(!rule?.remediationScript || disabled || loading || !device || !rule || impactState.loading || impactAvailability.checking || !impactAvailability.available) && 'disabled'}`}
                                onClick={(!rule?.remediationScript || disabled || loading || !device || !rule || impactState.loading || impactAvailability.checking || !impactAvailability.available) ? null : openImpactAnalysis}>Impact Analysis
                                </div>
                                <div className={`btn oct_btn-form modal-v2-btn modal-v2-btn-primary ${(!rule?.remediationScript || disabled) && 'disabled'}`}
                                onClick={disabled ? null : () => onExecute()}>Execute
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {impactState.open && (
                <ImpactAnalysisPopup
                    device={device}
                    rule={rule}
                    loading={impactState.loading}
                    error={impactState.error}
                    result={impactState.result}
                    serviceAvailable={impactAvailability.available}
                    onReassess={reassessImpact}
                    onProceed={onProceedToRemediation}
                    onClose={onCloseImpactAnalysis}
                    suspendClose={impactConfirmationOpen}

                />
            )}
        </div>
    )

}
